import { useMemo } from "react";
import { Ruler, Package, Layers, AlertCircle } from "lucide-react";
import {
  areDimensionsPlausible,
  DIMENSION_VERIFY_NOTICE,
  OTHER_SIZE_NOTICE,
  extractItemDims,
  flatFloorSize,
  titleAgreesWithDims,
  type FlatSize,
} from "@/lib/dimensions";
import {
  extractMaterials,
  extractFeatures,
  DIMENSIONS_UNAVAILABLE,
  MATERIALS_UNAVAILABLE,
} from "@/lib/productSpecs";
import { FREE_SHIPPING_OVER } from "@/lib/shipping";

// Convert an inch-based dim label like `98"` or `12.5 lbs` into a metric-suffixed string.
function withMetric(value: string): string {
  const inchMatch = value.match(/^(\d+\.?\d*)\s*["″]?$/);
  if (inchMatch) {
    const cm = Math.round(parseFloat(inchMatch[1]) * 2.54);
    return `${value.replace(/["″]?$/, '"')} (${cm} cm)`;
  }
  const lbMatch = value.match(/^(\d+\.?\d*)\s*lbs?$/i);
  if (lbMatch) {
    const kg = (parseFloat(lbMatch[1]) * 0.4536).toFixed(1);
    return `${value} (${kg} kg)`;
  }
  return value;
}


/** Long side × short side, when the supplier's width/depth labels can't be trusted. */
function footprintRows(dims: Record<string, string>): Array<[string, string]> {
  const n = (k: string) => parseFloat(dims[k] ?? "");
  const sides = [n("Width") || n("Length"), n("Depth")].filter((v) => v > 0).sort((a, b) => b - a);
  const rows: Array<[string, string]> = [];
  if (sides.length === 2) {
    rows.push(["Footprint", `${sides[0]}" × ${sides[1]}" (${Math.round(sides[0] * 2.54)} × ${Math.round(sides[1] * 2.54)} cm)`]);
  }
  for (const k of ["Height", "Seat Height", "Weight"]) if (dims[k]) rows.push([k, withMetric(dims[k])]);
  return rows;
}

/** A rug's size the way rugs are sold: short side × long side, in feet from 4 ft up. */
function flatRows(flat: FlatSize, dims: Record<string, string>, title: string): Array<[string, string]> {
  const inFeet = flat.long >= 48;
  const side = (n: number) => {
    if (!inFeet) return `${n}"`;
    const inches = Math.round(n);
    return inches % 12 ? `${Math.floor(inches / 12)}' ${inches % 12}"` : `${inches / 12}'`;
  };
  const cm = (n: number) => Math.round(n * 2.54);
  const round = flat.short === flat.long && /\bround\b/i.test(title);
  const rows: Array<[string, string]> = [
    [
      "Size",
      round
        ? `${side(flat.long)} round (${cm(flat.long)} cm)`
        : `${side(flat.short)} × ${side(flat.long)} (${cm(flat.short)} × ${cm(flat.long)} cm)`,
    ],
  ];
  if (flat.thickness) rows.push(["Thickness", `${flat.thickness}" (${Math.round(flat.thickness * 25.4)} mm)`]);
  if (dims.Weight) rows.push(["Weight", withMetric(dims.Weight)]);
  return rows;
}

interface DimensionsSectionProps {
  description: string;
  title: string;
  variantTitle?: string; // e.g. "98\" x 43\" x 30\""
  /** The supplier's product type: rugs and mats are measured as size and thickness. */
  productType?: string;
  /**
   * Whether the supplier's measurements describe the selected variant
   * (sizeFactsApply). When not, only a size the variant title states is shown.
   */
  sizeFactsApply?: boolean;
}

export const DimensionsSection = ({ description, title, variantTitle, productType, sizeFactsApply = true }: DimensionsSectionProps) => {
  // Prioritize variant title dimensions (from product name/size option) over description
  const dimensions = useMemo(
    () => extractItemDims(sizeFactsApply ? description : undefined, variantTitle),
    [description, variantTitle, sizeFactsApply]
  );
  // A title that leads with a size (113" Sofa…) names one of the item's axes.
  // If none of the parsed axes is that size, the feed is inconsistent: say so
  // instead of printing numbers that may belong to something else.
  // (Not while another size is selected: the title then names a different one.)
  const titleAgrees = useMemo(
    () => !sizeFactsApply || titleAgreesWithDims(title, dimensions),
    [dimensions, title, sizeFactsApply]
  );
  const dimensionsPlausible = useMemo(
    () => titleAgrees && areDimensionsPlausible(dimensions, title),
    [dimensions, title, titleAgrees]
  );
  const flat = useMemo(() => flatFloorSize(dimensions, title, productType), [dimensions, title, productType]);
  const materials = useMemo(() => extractMaterials(description), [description]);
  const features = useMemo(() => extractFeatures(description), [description]);

  // The section always renders. A stated gap is more useful to a furniture
  // shopper than a section that quietly disappears.
  return (
    <section className="border-t border-border">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-12 md:py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Dimensions — never asserted when the feed values are implausible */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Ruler className="h-5 w-5 text-foreground/40" />
              <p className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground font-medium">
                Dimensions
              </p>
            </div>
            <h2 className="font-sans text-lg font-semibold text-foreground mb-4">
              Size &amp; Measurements
            </h2>
            {!dimensions ? (
              <div className="flex items-start gap-2 rounded-xl bg-sand/40 p-3">
                <AlertCircle className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                <p className="text-[13px] text-foreground leading-relaxed">{sizeFactsApply ? DIMENSIONS_UNAVAILABLE : OTHER_SIZE_NOTICE}</p>
              </div>
            ) : !titleAgrees ? (
              <div className="flex items-start gap-2 rounded-xl bg-sand/40 p-3">
                <AlertCircle className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                <p className="text-[13px] text-foreground leading-relaxed">{DIMENSION_VERIFY_NOTICE}</p>
              </div>
            ) : flat ? (
              <>
                <div className="space-y-0">
                  {flatRows(flat, dimensions, title).map(([label, value]) => (
                    <div key={label} className="flex items-center justify-between py-3 border-b border-border gap-3">
                      <span className="text-[13px] text-muted-foreground">{label}</span>
                      <span className="text-[13px] font-semibold text-foreground text-right">{value}</span>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-muted-foreground mt-3">
                  All measurements are approximate. ±0.5" variance.
                </p>
              </>
            ) : !dimensionsPlausible ? (
              <>
                <div className="space-y-0">
                  {footprintRows(dimensions).map(([label, value]) => (
                    <div key={label} className="flex items-center justify-between py-3 border-b border-border gap-3">
                      <span className="text-[13px] text-muted-foreground">{label}</span>
                      <span className="text-[13px] font-semibold text-foreground text-right">{value}</span>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-muted-foreground mt-3">
                  The supplier's width, depth and height labels for this item look swapped, so the footprint is
                  shown as long side × short side. All measurements are approximate.
                </p>
              </>
            ) : (
              <>
                <div className="space-y-0">
                  {Object.entries(dimensions).map(([label, value]) => (
                    <div key={label} className="flex items-center justify-between py-3 border-b border-border gap-3">
                      <span className="text-[13px] text-muted-foreground">{label}</span>
                      <span className="text-[13px] font-semibold text-foreground text-right">{withMetric(value)}</span>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-muted-foreground mt-3">
                  All measurements are approximate. ±0.5" variance.
                </p>
              </>
            )}
          </div>

          {/* Materials */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Layers className="h-5 w-5 text-foreground/40" />
              <p className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground font-medium">
                Materials
              </p>
            </div>
            <h2 className="font-sans text-lg font-semibold text-foreground mb-4">
              What It's Made Of
            </h2>
            {materials.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {materials.map((mat) => (
                  <span key={mat} className="text-[12px] border border-border px-3 py-1.5 rounded-full text-foreground">
                    {mat}
                  </span>
                ))}
              </div>
            ) : (
              <div className="flex items-start gap-2 rounded-xl bg-sand/40 p-3">
                <AlertCircle className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                <p className="text-[13px] text-foreground leading-relaxed">{MATERIALS_UNAVAILABLE}</p>
              </div>
            )}
          </div>

          {/* Features — only phrases quoted from the supplier text */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Package className="h-5 w-5 text-foreground/40" />
              <p className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground font-medium">
                Features
              </p>
            </div>
            <h2 className="font-sans text-lg font-semibold text-foreground mb-4">
              Key Details
            </h2>
            {features.length > 0 ? (
              <ul className="space-y-2">
                {features.map((feat) => (
                  <li key={feat.label} className="flex items-center gap-2 text-[13px] text-foreground">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                    {feat.label}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="flex items-start gap-2 rounded-xl bg-sand/40 p-3">
                <AlertCircle className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                <p className="text-[13px] text-foreground leading-relaxed">
                  No feature details provided by the supplier for this item.
                </p>
              </div>
            )}
          </div>

          {/* Specifications */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Ruler className="h-5 w-5 text-foreground/40" />
              <p className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground font-medium">
                Specifications
              </p>
            </div>
            <h2 className="font-sans text-lg font-semibold text-foreground mb-4">
              Delivery & Returns
            </h2>
            <div className="space-y-0">
              <div className="flex items-center justify-between py-3 border-b border-border">
                <span className="text-[13px] text-muted-foreground">Free delivery</span>
                <span className="text-[13px] font-semibold text-foreground">{FREE_SHIPPING_OVER}</span>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-border">
                <span className="text-[13px] text-muted-foreground">Returns</span>
                <span className="text-[13px] font-semibold text-foreground">30 days</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
